import { isEmpty, map } from "lodash";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import Button from "antd/lib/button";
import Checkbox from "antd/lib/checkbox";
import routeWithUserSession from "@/components/ApplicationArea/routeWithUserSession";
import DynamicComponent from "@/components/DynamicComponent";
import DashboardGrid from "@/components/dashboards/DashboardGrid";
import Parameters from "@/components/Parameters";
import Filters from "@/components/Filters";
import { CrossCounterProvider } from "@/components/visualizations/CrossCounterContext";

import { Dashboard } from "@/services/dashboard";
import recordEvent from "@/services/recordEvent";
import resizeObserver from "@/services/resizeObserver";
import routes from "@/services/routes";
import location from "@/services/location";
import url from "@/services/url";
import useImmutableCallback from "@/lib/hooks/useImmutableCallback";

import useDashboard from "./hooks/useDashboard";
import DashboardHeader from "./components/DashboardHeader";

import "./DashboardPage.less";

import Tooltip from "antd/lib/tooltip";

function FilterSelector ({ allFilters, selectedFilters, onChange }) {
  if (isEmpty(allFilters)) return null;

  // 严格遵循传入的状态，不再做空数组回退全选的操作
  const currentValue = selectedFilters;

  const handleTagChange = (tag, checked) => {
    const nextSelectedTags = checked
      ? [...currentValue, tag]
      : currentValue.filter(t => t !== tag);
    onChange(nextSelectedTags);
  };

  return (
    <div className="filter-selector-wrapper">
      <div className="filter-selector-grid">
        {allFilters.map(item => {
          const isSelected = currentValue.includes(item.name);
          const isParam = !item.friendlyName.includes('::');
          return (
            <Tooltip title={item.name} key={item.name} mouseEnterDelay={0.5}>
              <div
                className={cx("filter-tile", { active: isSelected })}
                onClick={() => handleTagChange(item.name, !isSelected)}
              >
                <div className="tile-icon">
                  <i className={cx("zmdi", isParam ? "zmdi-format-list-bulleted" : "zmdi-filter-list")} />
                </div>
                <div className="tile-content">
                  <div className="tile-name">{item.friendlyName}</div>
                  <div className="tile-type">{isParam ? '参数' : '过滤器'}</div>
                </div>
                {isSelected && <div className="tile-check"><i className="zmdi zmdi-check" /></div>}
              </div>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}

FilterSelector.propTypes = {
  allFilters: PropTypes.array.isRequired,
  selectedFilters: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

function DashboardSettings({ dashboardConfiguration }) {
  const {
    dashboard,
    updateDashboard,
    allFilters,
    selectedFilters,
    setSelectedFilters,
  } = dashboardConfiguration;

  return (
    <div className="m-b-10 p-15 bg-white tiled">
      <Checkbox
        checked={!!dashboard.dashboard_filters_enabled}
        onChange={({ target }) => updateDashboard({ dashboard_filters_enabled: target.checked })}
        data-test="DashboardFiltersCheckbox">
        Use Dashboard Level Filters
      </Checkbox>

      <div className="public-filter-config-panel m-t-20">
        <div className="panel-header">
          <div className="header-title">
            <i className="zmdi zmdi-settings m-r-10" />
            公共分享页 - 筛选器展示配置
          </div>
          <div className="header-desc">选择要展示在点击分享链接后的页面上的筛选组件（未选中的项在该页面将被隐藏）</div>
        </div>
        <div className="panel-body">
          {isEmpty(allFilters) ? (
            <div className="empty-state">
              <i className="zmdi zmdi-info-outline" />
              <span>未检测到任何参数或过滤器</span>
            </div>
          ) : (
            <FilterSelector
              allFilters={allFilters}
              selectedFilters={selectedFilters}
              onChange={newSelectedFilters => {
                setSelectedFilters(newSelectedFilters);
                updateDashboard({
                  options: {
                    ...dashboard.options,
                    selectedFilters: newSelectedFilters,
                  },
                });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

DashboardSettings.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function AddWidgetContainer({ dashboardConfiguration, className, ...props }) {
  const { showAddTextboxDialog, showAddWidgetDialog } = dashboardConfiguration;
  return (
    <div className={cx("add-widget-container", className)} {...props}>
      <h2>
        <i className="zmdi zmdi-widgets" aria-hidden="true" />
        <span className="hidden-xs hidden-sm">
          Widgets are individual query visualizations or text boxes you can place on your dashboard in various
          arrangements.
        </span>
      </h2>
      <div>
        <Button className="m-r-15" onClick={showAddTextboxDialog} data-test="AddTextboxButton">
          Add Textbox
        </Button>
        <Button type="primary" onClick={showAddWidgetDialog} data-test="AddWidgetButton">
          Add Widget
        </Button>
      </div>
    </div>
  );
}

AddWidgetContainer.propTypes = {
  dashboardConfiguration: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
};

function DashboardComponent(props) {
  const dashboardConfiguration = useDashboard(props.dashboard, { isPublic: false });
  const {
    dashboard,
    filters,
    setFilters,
    loadDashboard,
    loadWidget,
    removeWidget,
    saveDashboardLayout,
    globalParameters,
    updateDashboard,
    refreshDashboard,
    refreshWidget,
    editingLayout,
    setGridDisabled,
  } = dashboardConfiguration;

  const [pageContainer, setPageContainer] = useState(null);
  const [bottomPanelStyles, setBottomPanelStyles] = useState({});
  const onParametersEdit = parameters => {
    const paramOrder = map(parameters, "name");
    updateDashboard({ options: { globalParamOrder: paramOrder } });
  };

  useEffect(() => {
    if (pageContainer) {
      const unobserve = resizeObserver(pageContainer, () => {
        if (editingLayout) {
          const style = window.getComputedStyle(pageContainer, null);
          const bounds = pageContainer.getBoundingClientRect();
          const paddingLeft = parseFloat(style.paddingLeft) || 0;
          const paddingRight = parseFloat(style.paddingRight) || 0;
          setBottomPanelStyles({
            left: Math.round(bounds.left) + paddingRight,
            width: pageContainer.clientWidth - paddingLeft - paddingRight,
          });
        }

        // reflow grid when container changes its size
        window.dispatchEvent(new Event("resize"));
      });
      return unobserve;
    }
  }, [pageContainer, editingLayout]);

  return (
    <CrossCounterProvider refreshing={dashboardConfiguration.refreshing}>
      <div className={cx("container", { "editing-mode": editingLayout })} ref={setPageContainer} data-test={`DashboardId${dashboard.id}Container`}>
        <DashboardHeader
          dashboardConfiguration={dashboardConfiguration}
          headerExtra={
            <DynamicComponent
              name="Dashboard.HeaderExtra"
              dashboard={dashboard}
              dashboardConfiguration={dashboardConfiguration}
            />
          }
        />
        {!isEmpty(globalParameters) && (
          <div className="dashboard-parameters m-b-10 p-15 bg-white tiled" data-test="DashboardParameters">
            <Parameters
              parameters={globalParameters}
              onValuesChange={refreshDashboard}
              sortable={editingLayout}
              onParametersEdit={onParametersEdit}
            />
          </div>
        )}
        {!isEmpty(filters) && (
          <div className="m-l-10 p-15 bg-white tiled" data-test="DashboardFilters">
            <Filters filters={filters} onChange={setFilters} />
          </div>
        )}
        {editingLayout && <DashboardSettings dashboardConfiguration={dashboardConfiguration} />}
        <div id="dashboard-container">
          <DashboardGrid
            dashboard={dashboard}
            widgets={dashboard.widgets}
            filters={filters}
            isEditing={editingLayout}
            onLayoutChange={editingLayout ? saveDashboardLayout : () => {}}
            onBreakpointChange={setGridDisabled}
            onLoadWidget={loadWidget}
            onRefreshWidget={refreshWidget}
            onRemoveWidget={removeWidget}
            onParameterMappingsChange={loadDashboard}
          />
        </div>
        {editingLayout && (
          <AddWidgetContainer dashboardConfiguration={dashboardConfiguration} style={bottomPanelStyles} />
        )}
      </div>
    </CrossCounterProvider>
  );
}

DashboardComponent.propTypes = {
  dashboard: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

function DashboardPage({ dashboardSlug, dashboardId, onError }) {
  const [dashboard, setDashboard] = useState(null);
  const handleError = useImmutableCallback(onError);

  useEffect(() => {
    Dashboard.get({ id: dashboardId, slug: dashboardSlug })
      .then(dashboardData => {
        recordEvent("view", "dashboard", dashboardData.id);
        setDashboard(dashboardData);

        // if loaded by slug, update location url to use the id
        if (!dashboardId) {
          location.setPath(url.parse(dashboardData.url).pathname, true);
        }
      })
      .catch(handleError);
  }, [dashboardId, dashboardSlug, handleError]);

  return <div className="dashboard-page">{dashboard && <DashboardComponent dashboard={dashboard} />}</div>;
}

DashboardPage.propTypes = {
  dashboardSlug: PropTypes.string,
  dashboardId: PropTypes.string,
  onError: PropTypes.func,
};

DashboardPage.defaultProps = {
  dashboardSlug: null,
  dashboardId: null,
  onError: PropTypes.func,
};

// route kept for backward compatibility
routes.register(
  "Dashboards.LegacyViewOrEdit",
  routeWithUserSession({
    path: "/dashboard/:dashboardSlug",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);

routes.register(
  "Dashboards.ViewOrEdit",
  routeWithUserSession({
    path: "/dashboards/:dashboardId([^-]+)(-.*)?",
    render: pageProps => <DashboardPage {...pageProps} />,
  })
);
